import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  ViewChild,
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { isPlatformBrowser } from "@angular/common";
import { ECoachChatSenderType, IChatMessage } from "../../../domain/models/coach/chat-message.model";

/** The hard cap of the message column; a lower one may be enforced by the server. */
const MAX_LENGTH = 1024;
/** How close to the bottom still counts as "reading the newest messages". */
const STICK_THRESHOLD = 80;

/**
 * A coaching conversation: the thread and the box to write in.
 *
 * Presentational on purpose — it neither calls the API nor holds a socket, because the coach panel
 * and the student app authenticate differently and talk to different paths. Both pass the same
 * messages in and listen for the same events out.
 */
@Component({
  selector: "app-chat-thread",
  templateUrl: "./chat-thread.component.html",
  styleUrl: "./chat-thread.component.scss",
})
export class ChatThreadComponent implements OnChanges, AfterViewChecked {
  @Input() title = "";
  @Input() subtitle?: string | null;
  /** Oldest first — the order the thread reads in. */
  @Input() messages: IChatMessage[] = [];
  /** Which side is "me": those messages sit on the right. */
  @Input() mySenderType: ECoachChatSenderType = ECoachChatSenderType.Coach;
  /** Label under the other side's messages, e.g. the student's first name. */
  @Input() counterpartName?: string | null;
  @Input() placeholder = "";
  @Input() emptyText = "";
  @Input() loadMoreText = "";
  @Input() isLoading = false;
  @Input() isSending = false;
  @Input() hasMore = false;
  @Input() disabled = false;
  /** Translation key of whatever the server refused, e.g. the anti-spam window. */
  @Input() errorKey?: string | null;

  @Output() sent = new EventEmitter<string>();
  @Output() loadMoreRequested = new EventEmitter<void>();

  @ViewChild("thread") thread?: ElementRef<HTMLElement>;

  bodyControl = new FormControl<string>("", [Validators.required, Validators.maxLength(MAX_LENGTH)]);
  maxLength = MAX_LENGTH;

  private readonly isBrowser: boolean;
  /** Set while the newest message is in view, so an arriving message scrolls down. */
  private stickToBottom = true;
  private lastMessageCount = 0;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnChanges(): void {
    // Older messages prepended while reading must not yank the thread to the bottom.
    if (this.messages.length > this.lastMessageCount + 1) {
      this.stickToBottom = this.lastMessageCount === 0;
    }
  }

  ngAfterViewChecked(): void {
    if (this.messages.length !== this.lastMessageCount) {
      this.lastMessageCount = this.messages.length;
      if (this.stickToBottom) {
        this.scrollToBottom();
      }
    }
  }

  isMine(message: IChatMessage): boolean {
    return message.senderType === this.mySenderType;
  }

  onScroll() {
    const thread = this.thread?.nativeElement;
    if (!thread) {
      return;
    }

    this.stickToBottom = thread.scrollHeight - thread.scrollTop - thread.clientHeight < STICK_THRESHOLD;
  }

  onSubmit() {
    const body = this.bodyControl.value?.trim();
    if (!body || this.bodyControl.invalid || this.isSending || this.disabled) {
      return;
    }

    this.stickToBottom = true;
    this.sent.emit(body);
    this.bodyControl.reset("");
  }

  /** Enter sends, Shift+Enter keeps writing. */
  onKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  private scrollToBottom() {
    if (!this.isBrowser) {
      return;
    }

    setTimeout(() => {
      const thread = this.thread?.nativeElement;
      if (thread) {
        thread.scrollTop = thread.scrollHeight;
      }
    });
  }
}
